import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFDocumentation from '../components/RMFDocumentation/RMFDocumentation';
import RMFFiscalCode from '../components/RMFFiscalCode/RMFFiscalCode';
import RMFGender from '../components/RMFGender/RMFGender';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPlaceOfBirth from '../components/RMFPlaceOfBirth/RMFPlaceOfBirth';
import RMFSecurityQuestions from '../components/RMFSecurityQuestions/RMFSecurityQuestions';
import RMFStandaloneFinalPage from '../components/RMFStandaloneFinalPage/RMFStandaloneFinalPage';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import useRMFBaseSteps from './useRMFBaseSteps';
import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfITFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const invokeRmfRealMoneyInformationCheck = new Invoke(
    Service.RmfRealMoneyInformationCheck,
    RMFLegalName
  );

  const invokeRmfDobUpdate = new Invoke(
    Service.RmfDobUpdate,
    invokeRmfRealMoneyInformationCheck,
    RMFDOB
  );

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate],
    [invokeRmfRealMoneyInformationCheck]
  );
  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, RMFGender, RMFLegalName);
  const invokeGenderSave = new Invoke(Service.RmfGenderSave, RMFFiscalCode, RMFGender);
  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFDocumentation, RMFAddress);

  const invokeFiscalCodeInfo = new Invoke(
    Service.RmfGetFiscalCodeInfo,
    RMFPlaceOfBirth,
    RMFFiscalCode
  );

  const invokePlaceOfBirthSave = new Invoke(
    Service.RmfPlaceOfBirthSave,
    RMFAddress,
    RMFPlaceOfBirth
  );

  const { forkSignupDepositCheckDone } = useRMFBaseSteps();

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [RMFVerification, forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      RMFStandaloneFinalPage,
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );

  const invokeEnableRealMoneyFlagItaly = new Invoke(
    Service.RmfEnableRealMoneyFlagItaly,
    forkRmfExit,
    RMFDepositLimit
  );

  const invokeSecurityQuestionsSave = new Invoke(
    Service.RmfSecurityQuestionsSave,
    RMFDepositLimit,
    RMFSecurityQuestions
  );

  const forkChallengeQuestionsCheck = new Fork(
    Guard.RmfChallengeQuestionsCheck,
    [RMFSecurityQuestions, invokeSecurityQuestionsSave],
    [RMFDepositLimit]
  );

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    invokeEnableRealMoneyFlagItaly,
    RMFDepositLimit
  );

  const invokeDocumentsUpdate = new Invoke(
    Service.RmfDocumentsUpdate,
    forkChallengeQuestionsCheck,
    RMFDocumentation
  );

  return [
    invokeSignupDob,
    forkDobCheck,
    RMFLegalName,
    invokeLegalNameSave,
    RMFGender,
    invokeGenderSave,
    RMFFiscalCode,
    invokeFiscalCodeInfo,
    RMFPlaceOfBirth,
    invokePlaceOfBirthSave,
    RMFAddress,
    invokeAddressSave,
    RMFDocumentation,
    invokeDocumentsUpdate,
    forkChallengeQuestionsCheck,
    RMFDepositLimit,
    invokeDepositLimitSave,
    invokeEnableRealMoneyFlagItaly,
    forkRmfExit,
    jumpToFeatureStep
  ];
};

export default useRmfITFlow;
